import { z } from 'zod';
import { ANKAUF_BETREFF_STANDARD, ANKAUF_TEXT_STANDARD, ANKAUF_TEXT_SPRACHEN, type AnkaufTextSprache } from './ankauf';

/**
 * E-Mails an Verkäufer, Schritte 1–7 des Ablaufs (Briefing „Erweiterung Ankauf“, Abschnitt 3 und 11).
 * Betreff und Text je Mail-Art und Sprache pflegt der Marktplatz (Ankauf → Einstellungen → E-Mail-Vorlagen),
 * sonst gelten die Standardtexte hier. Die Eingangsbestätigung auf Deutsch übernimmt Betreff und Text der
 * bisherigen Einstellung (ankauf_einstellung.betreff/text).
 *
 * Aufbau des Texts wie bei der Eingangsbestätigung: erste Zeile „Guten Tag …,“ wird zur Anrede, Absätze bleiben
 * Absätze, {link} wird zum Knopf ins Verkäuferportal (Einmal-Link mit Ablauf).
 */
export const ANKAUF_MAIL_ARTEN = {
  bestaetigung: 'Eingangsbestätigung',
  infos: 'Infos angefordert',
  angebot: 'Angebot',
  annahme: 'Ankauf vereinbart (Gegenangebot angenommen)',
  ablehnung: 'Anfrage abgelehnt',
  nachricht: 'Neue Nachricht',
  storno: 'Anfrage storniert',
  anmeldung: 'Anmelde-Link zum Verkäuferportal',
} as const;
export type AnkaufMailArt = keyof typeof ANKAUF_MAIL_ARTEN;
export const ANKAUF_MAIL_ART_CODES = Object.keys(ANKAUF_MAIL_ARTEN) as [AnkaufMailArt, ...AnkaufMailArt[]];

/** Platzhalter in Betreff und Text; {betrag}, {gueltig_bis}, {ankaufart} beim Angebot, {grund} bei Stornierung */
export const ANKAUF_MAIL_PLATZHALTER = [
  '{anrede}', '{vorname}', '{nachname}', '{nummer}', '{marke}', '{modell}', '{referenz}', '{betreiber}', '{betrag}', '{gueltig_bis}', '{ankaufart}', '{grund}', '{link}',
] as const;

type Standard = Record<AnkaufMailArt, { betreff: string; text: string }>;

const DE: Standard = {
  bestaetigung: { betreff: ANKAUF_BETREFF_STANDARD, text: ANKAUF_TEXT_STANDARD },
  infos: {
    betreff: 'Rückfrage zu Ihrer Verkaufsanfrage {nummer}',
    text: 'Guten Tag {anrede} {nachname},\n\nfür ein Angebot zu Ihrer {marke} {modell} benötigen wir noch einige Angaben. Bitte reichen Sie diese in Ihrem Verkäuferportal nach:\n{link}\n\nMit freundlichen Grüßen',
  },
  angebot: {
    betreff: 'Ihr Angebot zur Anfrage {nummer}',
    text: 'Guten Tag {anrede} {nachname},\n\nwir haben Ihre Angaben zur {marke} {modell} {referenz} geprüft und machen Ihnen folgendes Angebot ({ankaufart}):\n\n{betrag}\n\nDas Angebot gilt bis {gueltig_bis}. Sie können es in Ihrem Verkäuferportal annehmen, ein Gegenangebot machen oder ablehnen:\n{link}\n\nMit freundlichen Grüßen',
  },
  annahme: {
    betreff: 'Ankauf vereinbart – {nummer}',
    text: 'Guten Tag {anrede} {nachname},\n\nwir nehmen Ihr Gegenangebot über {betrag} an. Über die nächsten Schritte informieren wir Sie in Ihrem Verkäuferportal:\n{link}\n\nMit freundlichen Grüßen',
  },
  ablehnung: {
    betreff: 'Ihre Verkaufsanfrage {nummer}',
    text: 'Guten Tag {anrede} {nachname},\n\nvielen Dank für Ihr Vertrauen. Leider können wir Ihre {marke} {modell} nicht ankaufen.\n\nIhre Anfrage finden Sie weiterhin in Ihrem Verkäuferportal:\n{link}\n\nMit freundlichen Grüßen',
  },
  nachricht: {
    betreff: 'Neue Nachricht zu Ihrer Anfrage {nummer}',
    text: 'Guten Tag {anrede} {nachname},\n\nSie haben eine neue Nachricht zu Ihrer Verkaufsanfrage. Antworten können Sie in Ihrem Verkäuferportal:\n{link}\n\nMit freundlichen Grüßen',
  },
  storno: {
    betreff: 'Ihre Verkaufsanfrage {nummer} wurde storniert',
    text: 'Guten Tag {anrede} {nachname},\n\nIhre Verkaufsanfrage {nummer} wurde storniert. Grund: {grund}\n\nDen Verlauf finden Sie in Ihrem Verkäuferportal:\n{link}\n\nMit freundlichen Grüßen',
  },
  anmeldung: {
    betreff: 'Ihr Anmelde-Link bei {betreiber}',
    text: 'Guten Tag,\n\nmit diesem Link melden Sie sich in Ihrem Verkäuferportal an und sehen alle Ihre Verkaufsanfragen:\n{link}\n\nHaben Sie das nicht angefordert, können Sie diese E-Mail ignorieren.',
  },
};

const EN: Standard = {
  bestaetigung: {
    betreff: 'Your sell request {nummer}',
    text: 'Dear {anrede} {nachname},\n\nthank you for your request {nummer} to sell your {marke} {modell} {referenz}.\nOur team will review your details and get back to you with a personal offer within 48 hours.\n\nYou can follow your request and our offer here at any time:\n{link}\n\nKind regards',
  },
  infos: {
    betreff: 'Question about your sell request {nummer}',
    text: 'Dear {anrede} {nachname},\n\nto make you an offer for your {marke} {modell} we need a few more details. Please add them in your seller portal:\n{link}\n\nKind regards',
  },
  angebot: {
    betreff: 'Your offer for request {nummer}',
    text: 'Dear {anrede} {nachname},\n\nwe have reviewed the details of your {marke} {modell} {referenz} and are pleased to make you the following offer ({ankaufart}):\n\n{betrag}\n\nThe offer is valid until {gueltig_bis}. You can accept it, make a counter-offer or decline it in your seller portal:\n{link}\n\nKind regards',
  },
  annahme: {
    betreff: 'Sale agreed – {nummer}',
    text: 'Dear {anrede} {nachname},\n\nwe accept your counter-offer of {betrag}. We will keep you informed about the next steps in your seller portal:\n{link}\n\nKind regards',
  },
  ablehnung: {
    betreff: 'Your sell request {nummer}',
    text: 'Dear {anrede} {nachname},\n\nthank you for your trust. Unfortunately we are unable to purchase your {marke} {modell}.\n\nYour request remains available in your seller portal:\n{link}\n\nKind regards',
  },
  nachricht: {
    betreff: 'New message about your request {nummer}',
    text: 'Dear {anrede} {nachname},\n\nyou have a new message about your sell request. You can reply in your seller portal:\n{link}\n\nKind regards',
  },
  storno: {
    betreff: 'Your sell request {nummer} has been cancelled',
    text: 'Dear {anrede} {nachname},\n\nyour sell request {nummer} has been cancelled. Reason: {grund}\n\nYou can find the history in your seller portal:\n{link}\n\nKind regards',
  },
  anmeldung: {
    betreff: 'Your sign-in link for {betreiber}',
    text: 'Hello,\n\nuse this link to sign in to your seller portal and see all your sell requests:\n{link}\n\nIf you did not request this, you can ignore this email.',
  },
};

const STANDARD: Record<AnkaufTextSprache, Standard> = { de: DE, en: EN };

export function ankaufMailStandard(art: AnkaufMailArt, sprache: AnkaufTextSprache): { betreff: string; text: string } {
  return STANDARD[sprache][art];
}

/** Feste Teile der Mail je Sprache – nicht pflegbar */
export const ANKAUF_MAIL_FEST: Record<AnkaufTextSprache, { knopf: string; anmelden: string; anrede: { herr: string; frau: string }; nachricht: string; linkHinweis: string; anmeldeHinweis: string }> = {
  de: {
    knopf: 'Anfrage ansehen',
    anmelden: 'Jetzt anmelden',
    anrede: { herr: 'Herr', frau: 'Frau' },
    nachricht: 'Nachricht von {betreiber}:',
    linkHinweis: 'Der Link gilt bis {bis} und lässt sich einmal verwenden. Danach melden Sie sich im Verkäuferportal mit Ihrer E-Mail-Adresse an.',
    anmeldeHinweis: 'Der Link gilt {minuten} Minuten und lässt sich einmal verwenden.',
  },
  en: {
    knopf: 'View request',
    anmelden: 'Sign in now',
    anrede: { herr: 'Mr', frau: 'Ms' },
    nachricht: 'Message from {betreiber}:',
    linkHinweis: 'The link is valid until {bis} and can be used once. After that, sign in to the seller portal with your email address.',
    anmeldeHinweis: 'The link is valid for {minuten} minutes and can be used once.',
  },
};

/* ------------------------------------------------------------ Pflege im Konto */

export interface AnkaufMailVorlage {
  art: AnkaufMailArt;
  sprache: AnkaufTextSprache;
  betreff: string;
  text: string;
  /** keine eigene Fassung gespeichert – es gilt der Standardtext */
  standard: boolean;
}

/** Leerer Betreff und Text = zurück zum Standard */
export const AnkaufMailVorlageSchema = z.object({
  art: z.enum(ANKAUF_MAIL_ART_CODES),
  sprache: z.enum(ANKAUF_TEXT_SPRACHEN),
  betreff: z.string().trim().max(200).default(''),
  text: z.string().trim().max(5000).default(''),
});
export type AnkaufMailVorlageEingabe = z.input<typeof AnkaufMailVorlageSchema>;
